import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Download, Upload, FileSpreadsheet, CheckCircle2, AlertTriangle, Loader2 } from 'lucide-react';
import { api, formatApiError } from '@/lib/api';
import { toast } from 'sonner';

const downloadBlob = (content, filename, type) => {
  const url = URL.createObjectURL(new Blob([content], { type }));
  const a = document.createElement('a');
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
};

const SUITES = {
  talenttrain: {
    base: '/client/import/talenttrain',
    file: 'CTH_import_talenttrain.xlsx',
    title: { es: 'Importar acciones de capacitación (Excel)', en: 'Import training actions (Excel)' },
    desc: {
      es: 'Carga masiva de acciones de capacitación para tus colaboradores. Descarga la plantilla, complétala y previsualiza antes de confirmar.',
      en: 'Bulk-create training actions for your collaborators. Download the template, fill it in and preview before confirming.',
    },
    itemLabel: { es: 'acciones', en: 'actions' },
    rowTitle: (r) => `${r.employee} — ${r.training_name || r.name}`,
  },
  talentstart: {
    base: '/client/import/talentstart',
    file: 'CTH_import_talentstart.xlsx',
    title: { es: 'Importar onboarding (Excel)', en: 'Import onboarding (Excel)' },
    desc: {
      es: 'Asigna plantillas de onboarding existentes a tus colaboradores de forma masiva. Las etapas y responsables se copian desde la plantilla.',
      en: 'Bulk-assign existing onboarding templates to your collaborators. Stages and responsible parties are snapshotted from the template.',
    },
    itemLabel: { es: 'asignaciones', en: 'assignments' },
    rowTitle: (r) => `${r.employee} — ${r.template_name}`,
  },
  talentstart_templates: {
    base: '/client/import/talentstart_templates',
    file: 'CTH_import_talentstart_plantillas.xlsx',
    title: { es: 'Importar plantillas de onboarding (Excel)', en: 'Import onboarding templates (Excel)' },
    desc: {
      es: 'Crea plantillas de onboarding COMPLETAS (con sus etapas, responsables y horas) desde Excel. Distinto de la importación de asignaciones: aquí se crean las rutas, no se asignan. Las plantillas existentes con el mismo nombre no se sobrescriben.',
      en: 'Create COMPLETE onboarding templates (stages, responsible parties, hours) from Excel. Different from the assignment import: this creates the routes, it does not assign them. Existing templates with the same name are never overwritten.',
    },
    itemLabel: { es: 'plantillas', en: 'templates' },
    rowTitle: (r) => `${r.template_name}${r.stage_count ? ` (${r.stage_count} etapas)` : ''}`,
  },
  workforcerecords: {
    base: '/client/import/workforcerecords',
    file: 'CTH_import_workforcerecords.xlsx',
    title: { es: 'Importar registros laborales (Excel)', en: 'Import workforce records (Excel)' },
    desc: {
      es: 'Crea o actualiza registros documentales por colaborador y requisito. Las filas idénticas se omiten; las diferencias se aplican como actualización auditada.',
      en: 'Create or update document records per collaborator and requirement. Identical rows are skipped; differences are applied as audited updates.',
    },
    itemLabel: { es: 'registros', en: 'records' },
    rowTitle: (r) => `${r.employee} — ${r.requirement_name}`,
  },
};

const ERR_LABELS = {
  required_field: { es: 'Campo obligatorio', en: 'Required field' },
  invalid_email: { es: 'Correo inválido', en: 'Invalid email' },
  unknown_employee: { es: 'Colaborador no encontrado en tu empresa', en: 'Collaborator not found in your company' },
  inactive_employee: { es: 'Colaborador inactivo', en: 'Inactive collaborator' },
  former_collaborator_not_eligible: { es: 'Excolaborador — no elegible', en: 'Former collaborator — not eligible' },
  invalid_type: { es: 'Tipo inválido (course/seminar/workshop/coaching/elearning/other)', en: 'Invalid type (course/seminar/workshop/coaching/elearning/other)' },
  invalid_status: { es: 'Estado inválido (planned/in_progress/completed)', en: 'Invalid status (planned/in_progress/completed)' },
  unknown_competency: { es: 'Código de competencia desconocido', en: 'Unknown competency code' },
  invalid_date: { es: 'Fecha inválida (use AAAA-MM-DD)', en: 'Invalid date (use YYYY-MM-DD)' },
  end_before_start: { es: 'La fecha fin es anterior al inicio', en: 'End date precedes start date' },
  invalid_yes_no: { es: 'Valor inválido (Sí/No)', en: 'Invalid value (Yes/No)' },
  required_when_recert: { es: 'Fecha de recertificación obligatoria', en: 'Recertification date required' },
  duplicate_in_file: { es: 'Duplicado dentro del archivo', en: 'Duplicate inside the file' },
  unknown_template: { es: 'Plantilla no encontrada (nombre exacto, no archivada)', en: 'Template not found (exact name, not archived)' },
  unknown_requirement: { es: 'Requisito no encontrado (nombre exacto en Workforce Records)', en: 'Requirement not found (exact name in Workforce Records)' },
  invalid_training_type: { es: 'Tipo de etapa inválido (interview/workshop/practical_training/reading_assignment/external_training/certification/examination)', en: 'Invalid stage type' },
  invalid_number: { es: 'Número inválido', en: 'Invalid number' },
  invalid_status_wr: { es: 'Estado inválido (pending/compliant/na)', en: 'Invalid status (pending/compliant/na)' },
};

export function SuiteImportWizard({ suite, open, onClose, onImported }) {
  const { i18n } = useTranslation();
  const isES = (i18n.language || 'es').startsWith('es');
  const L = (obj) => (isES ? obj.es : obj.en);
  const Ltx = (es, en) => (isES ? es : en);
  const cfg = SUITES[suite];

  const fileRef = useRef(null);
  const [step, setStep] = useState('upload');
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);

  const reset = () => { setStep('upload'); setFile(null); setPreview(null); setResult(null); };
  const close = () => { reset(); onClose(); };
  const errLabel = (code) => (ERR_LABELS[code] ? L(ERR_LABELS[code]) : code);

  const downloadTemplate = async () => {
    try {
      const { data } = await api.get(`${cfg.base}/template`, { responseType: 'blob' });
      downloadBlob(data, cfg.file, 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const doPreview = async () => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await api.post(`${cfg.base}/preview`, fd);
      setPreview(data);
      setStep('preview');
    } catch (e) {
      toast.error(Ltx('Archivo inválido', 'Invalid file'), { description: formatApiError(e) });
    } finally { setBusy(false); }
  };

  const doImport = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`${cfg.base}/confirm`, {
        to_create: preview.to_create,
        ...(preview.to_update ? { to_update: preview.to_update } : {}),
      });
      setResult({ ...data, errors: [...(preview.errors || []), ...(data.errors || [])] });
      setStep('result');
      onImported?.();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) close(); }}>
      <DialogContent className="max-w-2xl" data-testid={`${suite}-import-dialog`}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSpreadsheet className="h-5 w-5 text-primary" /> {L(cfg.title)}
          </DialogTitle>
          <DialogDescription>{L(cfg.desc)}</DialogDescription>
        </DialogHeader>

        {step === 'upload' && (
          <div className="space-y-4 py-2">
            <Button variant="outline" onClick={downloadTemplate} data-testid={`${suite}-import-template-btn`}>
              <Download className="h-4 w-4 mr-1.5" /> {Ltx('Descargar plantilla Excel', 'Download Excel template')}
            </Button>
            <button
              type="button"
              className="w-full border-2 border-dashed rounded-lg p-8 text-center hover:bg-muted/30 transition-colors"
              onClick={() => fileRef.current?.click()}
              data-testid={`${suite}-import-dropzone`}
            >
              <Upload className="h-6 w-6 mx-auto text-muted-foreground mb-2" />
              <p className="text-sm font-medium">
                {file ? file.name : Ltx('Haz clic para seleccionar el archivo .xlsx', 'Click to select the .xlsx file')}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                {Ltx('Solo archivos Excel (.xlsx) basados en la plantilla', 'Excel files (.xlsx) based on the template only')}
              </p>
            </button>
            <input ref={fileRef} type="file" accept=".xlsx" className="hidden"
                   onChange={(e) => setFile(e.target.files?.[0] || null)}
                   data-testid={`${suite}-import-file-input`} />
          </div>
        )}

        {step === 'preview' && preview && (
          <div className="space-y-4 py-2">
            <div className="flex flex-wrap gap-2">
              <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200" data-testid={`${suite}-import-count-new`}>
                {preview.to_create.length} {Ltx('a crear', 'to create')}
              </Badge>
              {!!(preview.to_update || []).length && (
                <Badge className="bg-blue-100 text-blue-800 border-blue-200" data-testid={`${suite}-import-count-update`}>
                  {preview.to_update.length} {Ltx('a actualizar', 'to update')}
                </Badge>
              )}
              <Badge variant="outline" data-testid={`${suite}-import-count-skipped`}>
                {preview.skipped_existing.length} {Ltx('omitidas (ya existen)', 'skipped (already exist)')}
              </Badge>
              <Badge className={preview.errors.length ? 'bg-rose-100 text-rose-800 border-rose-200' : 'bg-muted'}
                     data-testid={`${suite}-import-count-errors`}>
                {preview.errors.length} {Ltx('filas con errores', 'rows with errors')}
              </Badge>
            </div>
            <div className="max-h-64 overflow-y-auto space-y-1.5 border rounded-lg p-3">
              {preview.to_create.map((r) => (
                <p key={`c${r.row}`} className="text-sm flex items-center gap-1.5" data-testid={`${suite}-import-row-${r.row}`}>
                  <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600 shrink-0" />
                  <span className="text-muted-foreground tabular-nums">#{r.row}</span> {cfg.rowTitle(r)}
                </p>
              ))}
              {(preview.to_update || []).map((r) => (
                <p key={`u${r.row}`} className="text-sm flex items-center gap-1.5 text-blue-700" data-testid={`${suite}-import-row-${r.row}`}>
                  <CheckCircle2 className="h-3.5 w-3.5 shrink-0" />
                  <span className="tabular-nums">#{r.row}</span> {cfg.rowTitle(r)} — {Ltx('actualización', 'update')}
                </p>
              ))}
              {preview.skipped_existing.map((r) => (
                <p key={`s${r.row}`} className="text-sm flex items-center gap-1.5 text-muted-foreground">
                  <AlertTriangle className="h-3.5 w-3.5 text-amber-500 shrink-0" />
                  <span className="tabular-nums">#{r.row}</span> {r.employee} — {Ltx('ya existe, se omite', 'already exists, skipped')}
                </p>
              ))}
              {preview.errors.map((e, i) => (
                <p key={`e${i}`} className="text-sm flex items-center gap-1.5 text-rose-700">
                  <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                  <span className="tabular-nums">#{e.row}</span> {e.column}: {errLabel(e.error)}
                </p>
              ))}
              {!preview.to_create.length && !preview.skipped_existing.length && !preview.errors.length && (
                <p className="text-sm italic text-muted-foreground">{Ltx('El archivo no contiene filas.', 'The file contains no rows.')}</p>
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              {Ltx('Solo se importarán las filas válidas marcadas "a crear". Nada se sobrescribe.',
                'Only the valid rows marked "to create" will be imported. Nothing is overwritten.')}
            </p>
          </div>
        )}

        {step === 'result' && result && (
          <div className="space-y-3 py-2" data-testid={`${suite}-import-result`}>
            <p className="text-sm font-medium flex items-center gap-1.5">
              <CheckCircle2 className="h-4 w-4 text-emerald-600" />
              {result.created.filter((r) => r.mode !== 'update').length} {L(cfg.itemLabel)} {Ltx('creadas', 'created')} ·{' '}
              {result.created.filter((r) => r.mode === 'update').length} {Ltx('actualizadas', 'updated')} ·{' '}
              {result.skipped_existing.length} {Ltx('omitidas', 'skipped')} ·{' '}
              {result.errors.length} {Ltx('rechazadas', 'rejected')}
            </p>
            <div className="max-h-56 overflow-y-auto space-y-1 border rounded-lg p-3">
              {result.created.map((r) => (
                <p key={r.row} className="text-sm">
                  <CheckCircle2 className="inline h-3.5 w-3.5 text-emerald-600 mr-1" />{cfg.rowTitle(r)}
                </p>
              ))}
              {result.errors.map((e, i) => (
                <p key={`re${i}`} className="text-sm text-rose-700">
                  <AlertTriangle className="inline h-3.5 w-3.5 mr-1" />#{e.row} {e.column}: {errLabel(e.error)}
                </p>
              ))}
            </div>
          </div>
        )}

        <DialogFooter>
          {step === 'upload' && (
            <>
              <Button variant="outline" onClick={close}>{Ltx('Cancelar', 'Cancel')}</Button>
              <Button onClick={doPreview} disabled={!file || busy} data-testid={`${suite}-import-preview-btn`}>
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : Ltx('Previsualizar', 'Preview')}
              </Button>
            </>
          )}
          {step === 'preview' && (
            <>
              <Button variant="outline" onClick={reset}>{Ltx('Elegir otro archivo', 'Choose another file')}</Button>
              <Button onClick={doImport} disabled={busy || (!preview.to_create.length && !(preview.to_update || []).length)}
                      className="bg-gradient-primary" data-testid={`${suite}-import-confirm-btn`}>
                {busy ? <Loader2 className="h-4 w-4 animate-spin" />
                  : `${Ltx('Confirmar importación', 'Confirm import')} (${preview.to_create.length + (preview.to_update || []).length})`}
              </Button>
            </>
          )}
          {step === 'result' && (
            <Button onClick={close} data-testid={`${suite}-import-close-btn`}>{Ltx('Cerrar', 'Close')}</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
